<template>
  <div class="p-4">
    <div class="mb-4">
      <h1 class="text-xl font-bold text-gray-900 dark:text-white mb-1">
        Gestión Visual de Asignaciones
      </h1>
      <p class="text-sm text-gray-600 dark:text-gray-400">
        Administra las asignaciones de usuarios y negocios de forma visual e intuitiva
      </p>
    </div>

    <!-- Pestañas de vista -->
    <div class="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 mb-4">
      <div class="border-b border-gray-200 dark:border-gray-700">
        <nav class="-mb-px flex">
          <button
            @click="activeTab = 'by-negocio'"
            :class="[
              'py-3 px-4 text-sm font-medium border-b-2 transition-colors',
              activeTab === 'by-negocio'
                ? 'border-violet-500 text-violet-600 dark:text-violet-400'
                : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
            ]"
          >
            <svg class="w-5 h-5 inline mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-4m-5 0H9m0 0H5m0 0h2M7 7h10M7 11h10M7 15h10"/>
            </svg>
            Por Negocio
          </button>
          <button
            @click="activeTab = 'by-usuario'"
            :class="[
              'py-3 px-4 text-sm font-medium border-b-2 transition-colors',
              activeTab === 'by-usuario'
                ? 'border-violet-500 text-violet-600 dark:text-violet-400'
                : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
            ]"
          >
            <svg class="w-5 h-5 inline mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z"/>
            </svg>
            Por Usuario
          </button>
        </nav>
      </div>
    </div>

    <!-- Vista Por Negocio -->
    <div v-if="activeTab === 'by-negocio'" class="space-y-4">
      <!-- Selector de negocio -->
      <div class="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
        <div class="p-4">
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Seleccionar Negocio
          </label>
          <select 
            v-model="selectedNegocioId"
            @change="loadUsuariosByNegocio"
            class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 dark:bg-gray-700 dark:text-white"
          >
            <option value="">Selecciona un negocio...</option>
            <option v-for="negocio in negocios" :key="negocio.id" :value="negocio.id">
              {{ negocio.nombre }} {{ negocio.activo ? '(Activo)' : '(Inactivo)' }}
            </option>
          </select>
        </div>
      </div>

      <!-- Panel de transferencia de usuarios -->
      <div v-if="selectedNegocioId" class="grid grid-cols-1 lg:grid-cols-5 gap-4">
        
        <!-- Usuarios NO asignados -->
        <div class="lg:col-span-2 bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
          <div class="p-3 border-b border-gray-200 dark:border-gray-700 bg-red-50 dark:bg-red-900/20">
            <h3 class="text-sm font-medium text-gray-900 dark:text-white flex items-center">
              <svg class="w-4 h-4 mr-2 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728L5.636 5.636m12.728 12.728L18.364 5.636M5.636 18.364l12.728-12.728"/>
              </svg>
              Usuarios Disponibles ({{ usuariosNoAsignados.length }})
            </h3>
          </div>
          <div 
            class="p-3 min-h-[300px] max-h-[500px] overflow-y-auto"
            @drop="handleDrop($event, 'unassigned')"
            @dragover="handleDragOver"
            @dragenter="handleDragEnter"
          >
            <div class="space-y-1">
              <div
                v-for="usuario in usuariosNoAsignados"
                :key="`no-${usuario.id}`"
                :draggable="true"
                @dragstart="handleDragStart($event, usuario, 'unassigned')"
                :class="[
                  'p-2 rounded-md border cursor-pointer transition-all duration-200',
                  selectedUnassignedUsers.includes(usuario.id)
                    ? 'bg-red-100 dark:bg-red-900/30 border-red-300 dark:border-red-600'
                    : 'bg-gray-50 dark:bg-gray-700 border-gray-200 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-600'
                ]"
                @click="toggleUnassignedSelection(usuario.id)"
              >
                <div class="flex items-center">
                  <input
                    type="checkbox"
                    :checked="selectedUnassignedUsers.includes(usuario.id)"
                    @click.stop
                    @change="toggleUnassignedSelection(usuario.id)"
                    class="mr-2 h-3 w-3 text-red-600 focus:ring-red-500 border-gray-300 rounded"
                  />
                  <div class="flex-1 min-w-0">
                    <p class="text-xs font-medium text-gray-900 dark:text-white truncate">
                      {{ usuario.nombre }} {{ usuario.apellidoPaterno }}
                    </p>
                    <p class="text-[10px] text-gray-500 dark:text-gray-400 truncate">{{ usuario.email }}</p>
                  </div>
                  <div class="ml-2">
                    <RolUsuario 
                      :rol-id="usuario.rol?.id || 0"
                      :nombre="usuario.rol?.nombre || 'Sin rol'"
                      :compact="true"
                    />
                  </div>
                </div>
              </div>
              <div v-if="usuariosNoAsignados.length === 0" class="text-center py-8 text-gray-400">
                <svg class="w-12 h-12 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                </svg>
                Todos los usuarios están asignados
              </div>
            </div>
          </div>
        </div>

        <!-- Controles de transferencia -->
        <div class="flex flex-col justify-center items-center space-y-3">
          <button
            @click="assignSelectedUsers"
            :disabled="selectedUnassignedUsers.length === 0 || loading"
            class="bg-green-600 hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white p-2 rounded-full transition-colors"
            title="Asignar usuarios seleccionados"
          >
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 7l5 5m0 0l-5 5m5-5H6"/>
            </svg>
          </button>
          
          <div class="text-center">
            <p class="text-[10px] text-gray-500 dark:text-gray-400">Arrastra y suelta</p>
            <p class="text-[10px] text-gray-500 dark:text-gray-400">o usa los botones</p>
          </div>

          <button
            @click="unassignSelectedUsers"
            :disabled="selectedAssignedUsers.length === 0 || loading"
            class="bg-red-600 hover:bg-red-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white p-2 rounded-full transition-colors"
            title="Remover usuarios seleccionados"
          >
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 17l-5-5m0 0l5-5m-5 5h12"/>
            </svg>
          </button>
        </div>

        <!-- Usuarios asignados -->
        <div class="lg:col-span-2 bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
          <div class="p-3 border-b border-gray-200 dark:border-gray-700 bg-green-50 dark:bg-green-900/20">
            <h3 class="text-sm font-medium text-gray-900 dark:text-white flex items-center">
              <svg class="w-4 h-4 mr-2 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
              </svg>
              Usuarios Asignados ({{ usuariosAsignados.length }})
            </h3>
          </div>
          <div 
            class="p-3 min-h-[300px] max-h-[500px] overflow-y-auto"
            @drop="handleDrop($event, 'assigned')"
            @dragover="handleDragOver"
            @dragenter="handleDragEnter"
          >
            <div class="space-y-1">
              <div
                v-for="usuario in usuariosAsignados"
                :key="`si-${usuario.id}`"
                :draggable="true"
                @dragstart="handleDragStart($event, usuario, 'assigned')"
                :class="[
                  'p-2 rounded-md border cursor-pointer transition-all duration-200',
                  selectedAssignedUsers.includes(usuario.id)
                    ? 'bg-green-100 dark:bg-green-900/30 border-green-300 dark:border-green-600'
                    : 'bg-gray-50 dark:bg-gray-700 border-gray-200 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-600'
                ]"
                @click="toggleAssignedSelection(usuario.id)"
              >
                <div class="flex items-center">
                  <input
                    type="checkbox"
                    :checked="selectedAssignedUsers.includes(usuario.id)"
                    @click.stop
                    @change="toggleAssignedSelection(usuario.id)"
                    class="mr-2 h-3 w-3 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                  />
                  <div class="flex-1 min-w-0">
                    <p class="text-xs font-medium text-gray-900 dark:text-white truncate">
                      {{ usuario.nombre }} {{ usuario.apellidoPaterno }}
                    </p>
                    <p class="text-[10px] text-gray-500 dark:text-gray-400 truncate">{{ usuario.email }}</p>
                  </div>
                  <div class="ml-2">
                    <RolUsuario 
                      :rol-id="usuario.rol?.id || 0"
                      :nombre="usuario.rol?.nombre || 'Sin rol'"
                      :compact="true"
                    />
                  </div>
                </div>
              </div>
              <div v-if="usuariosAsignados.length === 0" class="text-center py-8 text-gray-400">
                <svg class="w-12 h-12 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728L5.636 5.636m12.728 12.728L18.364 5.636M5.636 18.364l12.728-12.728"/>
                </svg>
                No hay usuarios asignados
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Vista Por Usuario -->
    <div v-if="activeTab === 'by-usuario'" class="space-y-4">
      <!-- Selector de usuario -->
      <div class="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
        <div class="p-4">
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Seleccionar Usuario
          </label>
          <select 
            v-model="selectedUsuarioId"
            @change="loadNegociosByUsuario"
            class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 dark:bg-gray-700 dark:text-white"
          >
            <option value="">Selecciona un usuario...</option>
            <option v-for="usuario in users" :key="usuario.id" :value="usuario.id">
              {{ usuario.nombre }} {{ usuario.apellidoPaterno }} - {{ usuario.email }}
            </option>
          </select>
        </div>
      </div>

      <!-- Panel de transferencia de negocios -->
      <div v-if="selectedUsuarioId" class="grid grid-cols-1 lg:grid-cols-5 gap-4">
        
        <!-- Negocios NO asignados -->
        <div class="lg:col-span-2 bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
          <div class="p-3 border-b border-gray-200 dark:border-gray-700 bg-red-50 dark:bg-red-900/20">
            <h3 class="text-sm font-medium text-gray-900 dark:text-white flex items-center">
              <svg class="w-4 h-4 mr-2 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728L5.636 5.636m12.728 12.728L18.364 5.636M5.636 18.364l12.728-12.728"/>
              </svg>
              Negocios Disponibles ({{ negociosNoAsignados.length }})
            </h3>
          </div>
          <div 
            class="p-3 min-h-[300px] max-h-[500px] overflow-y-auto"
            @drop="handleDropNegocio($event, 'unassigned')"
            @dragover.prevent
            @dragenter.prevent
          >
            <div class="space-y-2">
              <div
                v-for="negocio in negociosNoAsignados"
                :key="`no-neg-${negocio.id}`"
                :draggable="true"
                @dragstart="handleDragStartNegocio(negocio, 'unassigned')"
                :class="[
                  'p-3 rounded-lg border cursor-pointer transition-all duration-200',
                  selectedUnassignedNegocios.includes(negocio.id)
                    ? 'bg-red-100 dark:bg-red-900/30 border-red-300 dark:border-red-600'
                    : 'bg-gray-50 dark:bg-gray-700 border-gray-200 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-600'
                ]"
                @click="toggleUnassignedNegocioSelection(negocio.id)"
              >
                <div class="flex items-center">
                  <input
                    type="checkbox"
                    :checked="selectedUnassignedNegocios.includes(negocio.id)"
                    @click.stop
                    @change="toggleUnassignedNegocioSelection(negocio.id)"
                    class="mr-3 h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 rounded"
                  />
                  <div class="flex-1 min-w-0">
                    <p class="text-sm font-medium text-gray-900 dark:text-white truncate">
                      {{ negocio.nombre }}
                    </p>
                    <p class="text-xs" :class="negocio.activo ? 'text-green-600' : 'text-red-600'">
                      {{ negocio.activo ? 'Activo' : 'Inactivo' }}
                    </p>
                  </div>
                </div>
              </div>
              <div v-if="negociosNoAsignados.length === 0" class="text-center py-8 text-gray-400">
                <svg class="w-12 h-12 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                </svg>
                Todos los negocios están asignados
              </div>
            </div>
          </div>
        </div>

        <!-- Controles de transferencia -->
        <div class="flex flex-col justify-center items-center space-y-4">
          <button
            @click="assignSelectedNegocios"
            :disabled="selectedUnassignedNegocios.length === 0 || loading"
            class="bg-green-600 hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white p-3 rounded-full transition-colors"
            title="Asignar negocios seleccionados"
          >
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 7l5 5m0 0l-5 5m5-5H6"/>
            </svg>
          </button>
          
          <div class="text-center">
            <p class="text-xs text-gray-500 dark:text-gray-400">Arrastra y suelta</p>
            <p class="text-xs text-gray-500 dark:text-gray-400">o usa los botones</p>
          </div>

          <button
            @click="unassignSelectedNegocios"
            :disabled="selectedAssignedNegocios.length === 0 || loading"
            class="bg-red-600 hover:bg-red-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white p-3 rounded-full transition-colors"
            title="Remover negocios seleccionados"
          >
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 17l-5-5m0 0l5-5m-5 5h12"/>
            </svg>
          </button>
        </div>

        <!-- Negocios asignados -->
        <div class="lg:col-span-2 bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
          <div class="p-4 border-b border-gray-200 dark:border-gray-700 bg-green-50 dark:bg-green-900/20">
            <h3 class="font-semibold text-gray-900 dark:text-white flex items-center">
              <svg class="w-5 h-5 mr-2 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
              </svg>
              Negocios Asignados ({{ negociosAsignados.length }})
            </h3>
          </div>
          <div 
            class="p-4 min-h-[400px] max-h-[600px] overflow-y-auto"
            @drop="handleDropNegocio($event, 'assigned')"
            @dragover.prevent
            @dragenter.prevent
          >
            <div class="space-y-2">
              <div
                v-for="negocio in negociosAsignados"
                :key="`si-neg-${negocio.id}`"
                :draggable="true"
                @dragstart="handleDragStartNegocio(negocio, 'assigned')"
                :class="[
                  'p-3 rounded-lg border cursor-pointer transition-all duration-200',
                  selectedAssignedNegocios.includes(negocio.id)
                    ? 'bg-green-100 dark:bg-green-900/30 border-green-300 dark:border-green-600'
                    : 'bg-gray-50 dark:bg-gray-700 border-gray-200 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-600'
                ]"
                @click="toggleAssignedNegocioSelection(negocio.id)"
              >
                <div class="flex items-center">
                  <input
                    type="checkbox"
                    :checked="selectedAssignedNegocios.includes(negocio.id)"
                    @click.stop
                    @change="toggleAssignedNegocioSelection(negocio.id)"
                    class="mr-3 h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                  />
                  <div class="flex-1 min-w-0">
                    <p class="text-sm font-medium text-gray-900 dark:text-white truncate">
                      {{ negocio.nombre }}
                    </p>
                    <p class="text-xs" :class="negocio.activo ? 'text-green-600' : 'text-red-600'">
                      {{ negocio.activo ? 'Activo' : 'Inactivo' }}
                    </p>
                  </div>
                </div>
              </div>
              <div v-if="negociosAsignados.length === 0" class="text-center py-8 text-gray-400">
                <svg class="w-12 h-12 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728L5.636 5.636m12.728 12.728L18.364 5.636M5.636 18.364l12.728-12.728"/>
                </svg>
                No hay negocios asignados
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Loading overlay -->
    <div v-if="loading" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div class="bg-white dark:bg-gray-800 rounded-lg p-6 flex items-center">
        <svg class="animate-spin h-6 w-6 text-violet-600 mr-3" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"></path>
        </svg>
        <span class="text-gray-900 dark:text-white">Procesando...</span>
      </div>
    </div>

    <!-- Sistema de notificaciones -->
    <NotificationContainer />
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useHttp } from '@/composables/useHttp'
import { useNotifications } from '@/composables/useNotifications'
import NotificationContainer from '@/components/NotificationContainer.vue'
import RolUsuario from '@/components/RolUsuario.vue'

// Referencias reactivas
const activeTab = ref('by-negocio')
const users = ref([])
const negocios = ref([])

// Por negocio
const selectedNegocioId = ref('')
const usuariosAsignados = ref([])
const usuariosNoAsignados = ref([])
const selectedAssignedUsers = ref([])
const selectedUnassignedUsers = ref([])

// Por usuario
const selectedUsuarioId = ref('')
const negociosAsignados = ref([])
const negociosNoAsignados = ref([])
const selectedAssignedNegocios = ref([])
const selectedUnassignedNegocios = ref([])

// Drag and drop
const draggedItem = ref(null)
const dragAction = ref(null)

// Composable HTTP
const { get, post, loading, error, data } = useHttp()

// Sistema de notificaciones
const { showSuccess, showError } = useNotifications()

// Computed para usuario seleccionado
const selectedUsuario = computed(() => {
  return users.value.find(u => u.id == selectedUsuarioId.value)
})

const selectedNegocio = computed(() => {
  return negocios.value.find(n => n.id == selectedNegocioId.value)
})

// Funciones para cargar datos
const fetchUsers = async () => {
  try {
    await get('users')
    users.value = Array.isArray(data.value) ? data.value : data.value?.data || []
  } catch (err) {
    console.error('Error al cargar usuarios:', err)
    users.value = []
  }
}

const fetchNegocios = async () => {
  try {
    await get('negocios')
    negocios.value = Array.isArray(data.value) ? data.value : data.value?.data || []
  } catch (err) {
    console.error('Error al cargar negocios:', err)
    negocios.value = []
  }
}

// Cargar usuarios por negocio
const loadUsuariosByNegocio = async () => {
  if (!selectedNegocioId.value) return
  
  try {
    await get(`negocios/${selectedNegocioId.value}/usuarios`)
    usuariosAsignados.value = Array.isArray(data.value) ? data.value : data.value?.data || []
    
    // Usuarios no asignados = todos los usuarios - usuarios asignados
    const asignados = usuariosAsignados.value.map(u => u.id)
    usuariosNoAsignados.value = users.value.filter(u => !asignados.includes(u.id))
    
    // Limpiar selecciones
    selectedAssignedUsers.value = []
    selectedUnassignedUsers.value = []
  } catch (err) {
    console.error('Error al cargar usuarios del negocio:', err)
    usuariosAsignados.value = []
    usuariosNoAsignados.value = users.value
  }
}

// Cargar negocios por usuario
const loadNegociosByUsuario = async () => {
  if (!selectedUsuarioId.value) return
  
  try {
    await get(`users/${selectedUsuarioId.value}/negocios`)
    negociosAsignados.value = Array.isArray(data.value) ? data.value : data.value?.data || []
    
    // Negocios no asignados = todos los negocios - negocios asignados
    const asignados = negociosAsignados.value.map(n => n.id)
    negociosNoAsignados.value = negocios.value.filter(n => !asignados.includes(n.id))
    
    // Limpiar selecciones
    selectedAssignedNegocios.value = []
    selectedUnassignedNegocios.value = []
  } catch (err) {
    console.error('Error al cargar negocios del usuario:', err)
    negociosAsignados.value = []
    negociosNoAsignados.value = negocios.value
  }
}

// Funciones de selección para usuarios
const toggleAssignedSelection = (userId) => {
  const index = selectedAssignedUsers.value.indexOf(userId)
  if (index > -1) {
    selectedAssignedUsers.value.splice(index, 1)
  } else {
    selectedAssignedUsers.value.push(userId)
  }
}

const toggleUnassignedSelection = (userId) => {
  const index = selectedUnassignedUsers.value.indexOf(userId)
  if (index > -1) {
    selectedUnassignedUsers.value.splice(index, 1)
  } else {
    selectedUnassignedUsers.value.push(userId)
  }
}

// Funciones de selección para negocios
const toggleAssignedNegocioSelection = (negocioId) => {
  const index = selectedAssignedNegocios.value.indexOf(negocioId)
  if (index > -1) {
    selectedAssignedNegocios.value.splice(index, 1)
  } else {
    selectedAssignedNegocios.value.push(negocioId)
  }
}

const toggleUnassignedNegocioSelection = (negocioId) => {
  const index = selectedUnassignedNegocios.value.indexOf(negocioId)
  if (index > -1) {
    selectedUnassignedNegocios.value.splice(index, 1)
  } else {
    selectedUnassignedNegocios.value.push(negocioId)
  }
}

// Operaciones de asignación - usuarios
const assignSelectedUsers = async () => {
  if (selectedUnassignedUsers.value.length === 0) return
  
  const count = selectedUnassignedUsers.value.length // Guardar el count antes de limpiar
  
  try {
    const assignments = selectedUnassignedUsers.value.map(userId => ({
      usuario_id: userId,
      negocio_id: selectedNegocioId.value
    }))
    
    await post('usuario-negocio/bulk-assign', { assignments })
    await loadUsuariosByNegocio()
    
    showSuccess(
      'Asignación exitosa',
      `${count} usuario(s) asignado(s)`,
      4000,
      { compact: true }
    )
  } catch (err) {
    showError('Error', 'No se pudo asignar usuarios', 4000, { compact: true })
  }
}

const unassignSelectedUsers = async () => {
  if (selectedAssignedUsers.value.length === 0) return
  
  const count = selectedAssignedUsers.value.length // Guardar el count antes de limpiar
  
  try {
    const removals = selectedAssignedUsers.value.map(userId => ({
      usuario_id: userId,
      negocio_id: selectedNegocioId.value
    }))
    
    await post('usuario-negocio/bulk-remove', { removals })
    await loadUsuariosByNegocio()
    
    showSuccess(
      'Remoción exitosa',
      `${count} usuario(s) removido(s)`,
      4000,
      { compact: true }
    )
  } catch (err) {
    showError('Error', 'No se pudo remover usuarios', 4000, { compact: true })
  }
}

// Operaciones de asignación - negocios
const assignSelectedNegocios = async () => {
  if (selectedUnassignedNegocios.value.length === 0) return
  
  const count = selectedUnassignedNegocios.value.length // Guardar el count antes de limpiar
  
  try {
    const assignments = selectedUnassignedNegocios.value.map(negocioId => ({
      usuario_id: selectedUsuarioId.value,
      negocio_id: negocioId
    }))
    
    await post('usuario-negocio/bulk-assign', { assignments })
    await loadNegociosByUsuario()
    
    showSuccess(
      'Asignación exitosa',
      `${count} negocio(s) asignado(s)`,
      4000,
      { compact: true }
    )
  } catch (err) {
    showError('Error', 'No se pudo asignar negocios', 4000, { compact: true })
  }
}

const unassignSelectedNegocios = async () => {
  if (selectedAssignedNegocios.value.length === 0) return
  
  const count = selectedAssignedNegocios.value.length // Guardar el count antes de limpiar
  
  try {
    const removals = selectedAssignedNegocios.value.map(negocioId => ({
      usuario_id: selectedUsuarioId.value,
      negocio_id: negocioId
    }))
    
    await post('usuario-negocio/bulk-remove', { removals })
    await loadNegociosByUsuario()
    
    showSuccess(
      'Remoción exitosa',
      `${count} negocio(s) removido(s)`,
      4000,
      { compact: true }
    )
  } catch (err) {
    showError('Error', 'No se pudo remover negocios', 4000, { compact: true })
  }
}

// Drag and Drop - Usuarios
const handleDragStart = (event, usuario, action) => {
  console.log('Drag start:', usuario.nombre, action)
  draggedItem.value = usuario
  dragAction.value = action
  event.dataTransfer.setData('text/plain', usuario.id.toString())
  event.dataTransfer.effectAllowed = 'move'
}

const handleDragOver = (event) => {
  event.preventDefault()
  event.dataTransfer.dropEffect = 'move'
}

const handleDragEnter = (event) => {
  event.preventDefault()
}

const handleDrop = async (event, dropZone) => {
  event.preventDefault()
  console.log('Drop event:', dropZone, draggedItem.value, dragAction.value)
  
  if (!draggedItem.value || !dragAction.value) {
    console.log('No dragged item')
    return
  }
  
  // Determinar la acción basada en dónde viene y dónde va
  let shouldAssign = false
  let shouldUnassign = false
  
  if (dragAction.value === 'unassigned' && dropZone === 'assigned') {
    // Arrastrando desde disponibles a asignados -> ASIGNAR
    shouldAssign = true
    console.log('Will assign user to business')
  } else if (dragAction.value === 'assigned' && dropZone === 'unassigned') {
    // Arrastrando desde asignados a disponibles -> REMOVER
    shouldUnassign = true
    console.log('Will remove user from business')
  } else {
    // Misma zona, no hacer nada
    console.log('Same zone or invalid action, ignoring')
    draggedItem.value = null
    dragAction.value = null
    return
  }
  
  try {
    if (shouldAssign) {
      console.log('Assigning user to business')
      await post('usuario-negocio', {
        usuario_id: draggedItem.value.id,
        negocio_id: selectedNegocioId.value
      })
    } else if (shouldUnassign) {
      console.log('Removing user from business')
      await post('usuario-negocio/bulk-remove', {
        removals: [{
          usuario_id: draggedItem.value.id,
          negocio_id: selectedNegocioId.value
        }]
      })
    }
    
    await loadUsuariosByNegocio()
    
    const action = shouldAssign ? 'asignado' : 'removido'
    showSuccess(
      `Usuario ${action}`,
      `${draggedItem.value.nombre}`,
      3000,
      { compact: true }
    )
  } catch (err) {
    console.error('Drop error:', err)
    showError('Error', 'Operación fallida', 4000, { compact: true })
  }
  
  draggedItem.value = null
  dragAction.value = null
}

// Drag and Drop - Negocios
const handleDragStartNegocio = (negocio, action) => {
  console.log('Drag start negocio:', negocio.nombre, action)
  draggedItem.value = negocio
  dragAction.value = action
}

const handleDropNegocio = async (event, dropZone) => {
  event.preventDefault()
  console.log('Drop negocio event:', dropZone, draggedItem.value, dragAction.value)
  
  if (!draggedItem.value || !dragAction.value) {
    console.log('No dragged negocio')
    return
  }
  
  // Determinar la acción basada en dónde viene y dónde va
  let shouldAssign = false
  let shouldUnassign = false
  
  if (dragAction.value === 'unassigned' && dropZone === 'assigned') {
    // Arrastrando desde disponibles a asignados -> ASIGNAR
    shouldAssign = true
    console.log('Will assign negocio to user')
  } else if (dragAction.value === 'assigned' && dropZone === 'unassigned') {
    // Arrastrando desde asignados a disponibles -> REMOVER  
    shouldUnassign = true
    console.log('Will remove negocio from user')
  } else {
    // Misma zona, no hacer nada
    console.log('Same zone or invalid action, ignoring')
    draggedItem.value = null
    dragAction.value = null
    return
  }
  
  try {
    if (shouldAssign) {
      console.log('Assigning business to user')
      await post('usuario-negocio', {
        usuario_id: selectedUsuarioId.value,
        negocio_id: draggedItem.value.id
      })
    } else if (shouldUnassign) {
      console.log('Removing business from user')
      await post('usuario-negocio/bulk-remove', {
        removals: [{
          usuario_id: selectedUsuarioId.value,
          negocio_id: draggedItem.value.id
        }]
      })
    }
    
    await loadNegociosByUsuario()
    
    const action = shouldAssign ? 'asignado' : 'removido'
    showSuccess(
      `Negocio ${action}`,
      `${draggedItem.value.nombre}`,
      3000,
      { compact: true }
    )
  } catch (err) {
    console.error('Drop negocio error:', err)
    showError('Error', 'Operación fallida', 4000, { compact: true })
  }
  
  draggedItem.value = null
  dragAction.value = null
}


// Cargar datos al montar el componente
onMounted(() => {
  fetchUsers()
  fetchNegocios()
})
</script>